import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchRunnerRequestBootstrap } from '../../lib/api/runnerRequestBootstrap';
import { queryKeys } from './queryKeys';

/**
 * 러너 요청 모달 콜드 오픈 시 병렬로 나가던 8개 소스를 단일 부트스트랩 요청으로 묶는다.
 * 응답을 각 개별 캐시 키에 그대로 하이드레이트해, 같은 키를 쓰는 기존 훅들이 재요청 없이
 * 따뜻한 캐시를 읽게 한다(9→1). 개별 훅은 부트스트랩이 끝난 뒤 enable되어야 재페치를 피할
 * 수 있으므로, 호출부(AppProviders)에서 isSuccess/isError로 게이팅한다.
 *
 * 하이드레이션은 반드시 queryFn 안에서 데이터를 반환하기 "전"에 수행한다. useEffect(커밋 이후)에서
 * 하면, 부트스트랩이 성공을 노출한 렌더 패스에서 개별 훅이 enabled=true + 빈 캐시를 관찰해 각자
 * 네트워크 요청을 시작할 수 있다(9→1 회귀). queryFn에서 먼저 setQueryData하면 success가 노출되는
 * 시점엔 개별 캐시가 이미 채워져 있어 개별 훅이 곧바로 따뜻한 캐시를 읽는다.
 */
export const useRunnerRequestBootstrap = (
  token: string | null,
  projectId: string,
  memberId: string | null,
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.runnerRequestBootstrap.all(projectId),
    queryFn: async () => {
      const data = await fetchRunnerRequestBootstrap(token as string, projectId);

      // 성공 노출 전에 각 개별 캐시를 채운다. 키는 개별 훅이 쓰는 것과 정확히 동일해야 한다.
      queryClient.setQueryData(queryKeys.daemons.all, {
        daemons: data.daemons,
        onlineThresholdMs: data.onlineThresholdMs,
      });
      queryClient.setQueryData(queryKeys.daemons.shared(projectId), data.sharedDaemons);
      queryClient.setQueryData(queryKeys.runnerModels.public, data.publicRunnerModels);
      if (memberId) {
        queryClient.setQueryData(queryKeys.runnerModels.merged(memberId), data.mergedRunnerModels);
      }
      queryClient.setQueryData(queryKeys.runnerModels.catalogs(projectId), data.runnerModelCatalogs);
      for (const [daemonId, catalog] of Object.entries(data.runnerModelCatalogs)) {
        queryClient.setQueryData(queryKeys.runnerModels.forDaemon(projectId, daemonId), catalog);
      }
      queryClient.setQueryData(queryKeys.agentConfigs.all(projectId), data.agentConfigs);
      queryClient.setQueryData(queryKeys.heartbeat.all(projectId), data.heartbeat);
      // useActiveDaemonTriggers는 envelope({data,meta})를 캐시하고 select로 배열만 노출한다.
      queryClient.setQueryData(
        queryKeys.daemonTriggers.list({ status: 'active', projectId, limit: 100 }),
        data.activeDaemonTriggers,
      );
      // AppProviders의 useConventions 호출과 동일한 파라미터({ pageSize: 100 })를 사용한다.
      queryClient.setQueryData(queryKeys.conventions.list(projectId, { pageSize: 100 }), data.conventions);

      return data;
    },
    enabled: Boolean(token) && projectId.length > 0 && (options?.enabled ?? true),
    staleTime: 60_000,
    gcTime: 1_800_000,
  });
};
